import { useCallback, useEffect, useState } from 'react';

export type ApiViewState = 'loading' | 'empty' | 'error' | 'ready';

export interface ApiQueryResult<T> {
  data: T;
  error: string;
  reload: () => void;
  state: ApiViewState;
}

export function useApiQuery<T>(options: {
  preview: boolean;
  previewData: T;
  previewError?: string;
  previewState?: ApiViewState;
  emptyData?: T;
  load: () => Promise<T>;
  isEmpty?: (data: T) => boolean;
  deps?: readonly unknown[];
}): ApiQueryResult<T> {
  const [data, setData] = useState<T>(() => options.preview ? previewData(options) : emptyData(options));
  const [error, setError] = useState(() => options.preview && options.previewState === 'error' ? options.previewError || 'Unable to load this screen.' : '');
  const [state, setState] = useState<ApiViewState>(options.preview ? previewState(options) : 'loading');
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => setReloadKey((value) => value + 1), []);

  useEffect(() => {
    let active = true;
    if (options.preview) {
      setData(previewData(options));
      setError(options.previewState === 'error' ? options.previewError || 'Unable to load this screen.' : '');
      setState(previewState(options));
      return () => {
        active = false;
      };
    }

    setData(emptyData(options));
    setState('loading');
    setError('');
    options.load()
      .then((value) => {
        if (!active) return;
        setData(value);
        setState(stateForData(value, options.isEmpty));
      })
      .catch((err) => {
        if (!active) return;
        setError(err instanceof Error ? err.message : 'Unable to load this screen.');
        setState('error');
      });
    return () => {
      active = false;
    };
  }, [options.preview, options.previewState, reloadKey, ...(options.deps || [])]);

  return { data, error, reload, state };
}

function previewState<T>(options: { previewData: T; previewState?: ApiViewState; isEmpty?: (data: T) => boolean }): ApiViewState {
  return options.previewState || stateForData(options.previewData, options.isEmpty);
}

function previewData<T>(options: { previewData: T; previewState?: ApiViewState; emptyData?: T }): T {
  if (options.previewState === 'empty') return emptyData(options);
  return options.previewData;
}

function emptyData<T>(options: { emptyData?: T; previewData: T }): T {
  if (options.emptyData !== undefined) return options.emptyData;
  return emptyDataLike(options.previewData);
}

function stateForData<T>(data: T, isEmpty?: (data: T) => boolean): ApiViewState {
  return isEmpty?.(data) ? 'empty' : 'ready';
}

function emptyDataLike<T>(data: T): T {
  if (Array.isArray(data)) return [] as T;
  if (data && typeof data === 'object' && Array.isArray((data as { items?: unknown }).items)) {
    return { ...(data as Record<string, unknown>), items: [] } as T;
  }
  throw new Error('useApiQuery requires emptyData for non-list previewData.');
}
